import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, LifeBuoy, Mail, Plus } from 'lucide-react';
import {
  createAdminSupportTicket,
  fetchAdminSupportTicket,
  fetchAdminSupportTickets,
  patchAdminSupportTicket,
  type AdminTicketRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function categoryLabel(category: string) {
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function CreateTicketModal({ open, onClose, categories }: { open: boolean; onClose: () => void; categories: string[] }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject: '', userEmail: '', userName: '', description: '', priority: 'medium', category: categories[0] ?? 'general' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({ subject: '', userEmail: '', userName: '', description: '', priority: 'medium', category: categories[0] ?? 'general' });
    setError('');
  }, [open, categories]);

  const create = useMutation({
    mutationFn: () => createAdminSupportTicket({
      subject: form.subject.trim(),
      userEmail: form.userEmail.trim(),
      userName: form.userName.trim() || undefined,
      description: form.description.trim() || undefined,
      priority: form.priority,
      category: form.category,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-support'] }); onClose(); },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create ticket'),
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="wfx-admin-modal glass-surface" role="dialog" aria-labelledby="create-ticket-title" onClick={(e) => e.stopPropagation()}>
        <header className="wfx-admin-modal-head"><div><p className="wfx-admin-breadcrumb">Support queue</p><h2 id="create-ticket-title"><LifeBuoy size={20} /> New ticket</h2></div></header>
        <form className="wfx-admin-form" onSubmit={(e) => { e.preventDefault(); setError(''); create.mutate(); }}>
          <label className="wfx-admin-field"><span>Subject</span><input required value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} /></label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field"><span>User email</span><input required type="email" value={form.userEmail} onChange={(e) => setForm((f) => ({ ...f, userEmail: e.target.value }))} /></label>
            <label className="wfx-admin-field"><span>User name</span><input value={form.userName} onChange={(e) => setForm((f) => ({ ...f, userName: e.target.value }))} /></label>
          </div>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field"><span>Category</span><select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>{categories.map((cat) => <option key={cat} value={cat}>{categoryLabel(cat)}</option>)}</select></label>
            <label className="wfx-admin-field"><span>Priority</span><select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
          </div>
          <label className="wfx-admin-field"><span>Description</span><textarea rows={4} className="wfx-admin-textarea" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions"><button type="button" className="secondary-action" onClick={onClose}>Cancel</button><button type="submit" className="primary-action" disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create ticket'}</button></div>
        </form>
      </div>
    </div>
  );
}

function TicketDetailCard({ ticketId, categories, statuses, onClose }: { ticketId: string | null; categories: string[]; statuses: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['admin-support-ticket', ticketId], queryFn: () => fetchAdminSupportTicket(ticketId!), enabled: Boolean(ticketId) });
  const update = useMutation({
    mutationFn: (body: Partial<AdminTicketRow>) => patchAdminSupportTicket(ticketId!, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-support'] }); void qc.invalidateQueries({ queryKey: ['admin-support-ticket', ticketId] }); },
  });
  const row = data?.ticket;

  return (
    <AdminDetailCard open={Boolean(ticketId)} onClose={onClose} ariaLabel="Support ticket" breadcrumb="Ticket details" title={row?.subject ?? 'Loading…'} illustration="admin.module.support"
      subtitle={row ? (<><AdminStatusBadge status={row.status} /><AdminStatusBadge status={row.priority} /><span className="wfx-muted wfx-admin-subline">{categoryLabel(row.category)}</span></>) : undefined}>
      {isLoading && <LoadingState label="Loading ticket…" />}
      {isError && <ErrorState message="Could not load ticket." onRetry={() => void refetch()} />}
      {row && (
        <>
          {row.description && <p className="wfx-admin-detail-message">{row.description}</p>}
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>User</dt><dd>{row.userName ?? '—'} · {row.userEmail}</dd></div>
            <div><dt>Assigned to</dt><dd>{row.assignedTo ?? 'Unassigned'}</dd></div>
            <div><dt>Created</dt><dd>{new Date(row.createdAt).toLocaleString()}</dd></div>
            <div><dt>Resolved</dt><dd>{row.resolvedAt ? new Date(row.resolvedAt).toLocaleString() : '—'}</dd></div>
          </dl>
          <div className="wfx-admin-form">
            <div className="wfx-admin-form-row">
              <label className="wfx-admin-field"><span>Status</span><select defaultValue={row.status} onChange={(e) => update.mutate({ status: e.target.value })}>{statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></label>
              <label className="wfx-admin-field"><span>Priority</span><select defaultValue={row.priority} onChange={(e) => update.mutate({ priority: e.target.value })}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
            </div>
            <label className="wfx-admin-field"><span>Assigned to</span><input defaultValue={row.assignedTo ?? ''} placeholder="support-team" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (row.assignedTo ?? '')) update.mutate({ assignedTo: v || null }); }} /></label>
            <label className="wfx-admin-field"><span>Category</span><select defaultValue={row.category} onChange={(e) => update.mutate({ category: e.target.value })}>{categories.map((cat) => <option key={cat} value={cat}>{categoryLabel(cat)}</option>)}</select></label>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function TicketCard({ row, onSelect }: { row: AdminTicketRow; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-support-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head"><AdminStatusBadge status={row.status} /><AdminStatusBadge status={row.priority} /></div>
      <strong>{row.subject}</strong>
      <span className="wfx-muted wfx-admin-result-org"><Mail size={14} /> {row.userName ?? row.userEmail}</span>
      <div className="wfx-admin-page-card-stats"><span>{categoryLabel(row.category)}</span><span>{new Date(row.createdAt).toLocaleDateString()}</span></div>
    </button>
  );
}

export function AdminSupportScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300); return () => window.clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [debouncedQuery, statusFilter, priorityFilter, categoryFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-support', debouncedQuery, statusFilter, priorityFilter, categoryFilter, page],
    queryFn: () => fetchAdminSupportTickets({ q: debouncedQuery || undefined, status: statusFilter, priority: priorityFilter, category: categoryFilter, page, limit: 12 }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const categories = data?.categories ?? [];
  const statuses = data?.statuses ?? [];
  const pagination = data?.pagination;
  const kpiItems = useMemo(() => [
    { label: 'Open', value: stats?.open ?? 0 },
    { label: 'In progress', value: stats?.inProgress ?? 0 },
    { label: 'Resolved today', value: stats?.resolvedToday ?? 0 },
    { label: 'Avg response', value: `${stats?.avgResponseMinutes ?? 0}m` },
  ], [stats]);

  if (isLoading && !data) return <LoadingState label="Loading support tickets…" />;
  if (isError) return <ErrorState message="Could not load support." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout breadcrumb="Admin / Support Tickets" title="Support Tickets" description="Track and resolve student support requests across vault, results, and account issues." icon={LifeBuoy} illustration="admin.module.support"
        actions={<button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}><Plus size={16} /> New ticket</button>}>
        <AdminKpiStrip items={kpiItems} />
        <div className="wfx-admin-blog-grid">{rows.map((row) => <TicketCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />)}</div>
        {rows.length === 0 && <AdminEmptyState message="No tickets match your filters." />}
        <AdminTablePanel title="Ticket queue" toolbar={(
          <div className="wfx-admin-toolbar">
            <AdminSearchInput value={query} onChange={setQuery} placeholder="Search subject, email, or name…" />
            <select className="wfx-admin-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
              <option value="all">All status</option>{statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            <select className="wfx-admin-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} aria-label="Filter by priority">
              <option value="all">All priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
            <select className="wfx-admin-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
              <option value="all">All categories</option>{categories.map((cat) => <option key={cat} value={cat}>{categoryLabel(cat)}</option>)}
            </select>
          </div>
        )} footer={pagination && pagination.pages > 1 ? (
          <div className="wfx-admin-pagination">
            <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="wfx-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} tickets</span>
            <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        ) : undefined}>
          <table className="wfx-admin-table"><thead><tr><th>Subject</th><th>User</th><th>Category</th><th>Status</th><th>Priority</th><th>Created</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id} className="wfx-admin-table-row-clickable" onClick={() => setSelectedId(row.id)}>
                <td><strong>{row.subject}</strong></td><td>{row.userName ?? row.userEmail}</td><td>{categoryLabel(row.category)}</td><td><AdminStatusBadge status={row.status} /></td><td><AdminStatusBadge status={row.priority} /></td><td>{new Date(row.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>
      <CreateTicketModal open={createOpen} onClose={() => setCreateOpen(false)} categories={categories} />
      <TicketDetailCard ticketId={selectedId} categories={categories} statuses={statuses} onClose={() => setSelectedId(null)} />
    </>
  );
}
